import * as Dialog from "@radix-ui/react-dialog";
import {
	BarChart2,
	Coins,
	FlaskConical,
	MapPin,
	Package,
	Palette,
	PlayCircle,
	Scroll,
	Settings,
	Sword,
	X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useNavigate, useParams } from "react-router";
import { AppHeader } from "@/components/layout/AppHeader";
import { RouteFallback } from "@/components/RouteFallback";
import { useAuth } from "@/components/AuthContext";
import { ValidationBanner } from "@/components/ValidationBanner";
import { Kitsu } from "@/components/Kitsu";
import { ProjectDataProvider } from "@/hooks/ProjectDataProvider";
import { useProjectData } from "@/hooks/useProjectData";
import { apiClient, isApiError } from "@/lib/apiClient";
import { logError } from "@/lib/logger";
import type { Project } from "@/lib/types";

const CMS_TOUR_VIDEO_URL = "https://www.youtube-nocookie.com/embed/wl8BoBTxmxQ?rel=0";

interface NavGroup {
	label: string;
	items: {
		key: string;
		Icon: LucideIcon;
		label: string;
		countKey?: string;
	}[];
}

const NAV_GROUPS: NavGroup[] = [
	{
		label: "nav.sectionContent",
		items: [
			{ key: "items", Icon: Package, label: "nav.items", countKey: "items" },
			{ key: "attacks", Icon: Sword, label: "nav.attacks", countKey: "attacks" },
			{
				key: "recipes",
				Icon: FlaskConical,
				label: "nav.recipes",
				countKey: "recipes",
			},
		],
	},
	{
		label: "nav.sectionEconomy",
		items: [
			{
				key: "currencies",
				Icon: Coins,
				label: "nav.currencies",
				countKey: "currencies",
			},
			{ key: "stats", Icon: BarChart2, label: "nav.stats", countKey: "stats" },
		],
	},
	{
		label: "nav.sectionGameplay",
		items: [
			{
				key: "quests",
				Icon: Scroll,
				label: "nav.quests",
				countKey: "quests",
			},
			{
				key: "places",
				Icon: MapPin,
				label: "nav.places",
				countKey: "places",
			},
			{
				key: "design",
				Icon: Palette,
				label: "nav.design",
			},
		],
	},
	{
		label: "nav.sectionSettings",
		items: [
			{ key: "settings", Icon: Settings, label: "nav.settings" },
		],
	},
];

function CmsTourDialog({
	open,
	onClose,
}: {
	open: boolean;
	onClose: () => void;
}) {
	const { t } = useTranslation();

	return (
		<Dialog.Root open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
			<Dialog.Portal>
				<Dialog.Overlay
					className="fixed inset-0 z-[60]"
					style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
				/>
				<Dialog.Content
					className="fixed left-1/2 top-1/2 z-[70] w-[min(920px,92vw)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg"
					style={{
						background: "#141415",
						border: "1px solid rgba(255,255,255,0.08)",
						boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
					}}
				>
					<div
						className="flex items-start justify-between gap-4 p-5 md:p-6"
						style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
					>
						<div className="flex items-start gap-3 min-w-0">
							<div
								className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
								style={{
									background: "rgba(139, 92, 246, 0.12)",
									color: "#8b5cf6",
								}}
							>
								<PlayCircle size={20} />
							</div>
							<div className="min-w-0">
								<p
									className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1"
									style={{ color: "#8b5cf6" }}
								>
									{t("project.cmsTourEyebrow")}
								</p>
								<Dialog.Title
									className="text-xl md:text-2xl font-bold"
									style={{ color: "#e4e4e7" }}
								>
									{t("project.cmsTourTitle")}
								</Dialog.Title>
								<Dialog.Description
									className="mt-2 text-sm leading-6"
									style={{ color: "#a1a1aa" }}
								>
									{t("project.cmsTourDescription")}
								</Dialog.Description>
							</div>
						</div>
						<Dialog.Close asChild>
							<button
								type="button"
								className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors"
								style={{ color: "#71717a" }}
								aria-label={t("common.close")}
							>
								<X size={18} />
							</button>
						</Dialog.Close>
					</div>
					<div className="aspect-video w-full" style={{ background: "#050506" }}>
						<iframe
							className="w-full h-full"
							src={CMS_TOUR_VIDEO_URL}
							title={t("project.cmsTourVideoTitle")}
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
							allowFullScreen
							loading="lazy"
							referrerPolicy="strict-origin-when-cross-origin"
						/>
					</div>
					<div className="flex justify-end p-5 md:p-6">
						<Dialog.Close asChild>
							<button
								type="button"
								className="btn-pill btn-pill-primary text-sm"
							>
								{t("project.cmsTourDone")}
							</button>
						</Dialog.Close>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

interface SidebarNavProps {
	slug: string | undefined;
	counts: Record<string, number>;
	onNavigate?: () => void;
}

function SidebarNav({ slug, counts, onNavigate }: SidebarNavProps) {
	const { t } = useTranslation();
	return (
		<>
			{NAV_GROUPS.map((group) => (
				<div key={group.label} className="mb-5">
					<h4
						className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5 px-2.5"
						style={{ color: "#8b5cf6" }}
					>
						{t(group.label)}
					</h4>
					{group.items.map(({ key, Icon, label, countKey }) => {
						const count = countKey ? counts[countKey] : undefined;
						return (
							<NavLink
								key={key}
								to={`/projects/${slug ?? ""}/${key}`}
								className="block"
								onClick={onNavigate}
							>
								{({ isActive }) => (
									<div
										className="flex items-center gap-2.5 px-2.5 py-[6px] rounded-lg text-[13px] transition-all duration-150"
										style={{
											color: isActive ? "#e4e4e7" : "#71717a",
											background: isActive
												? "rgba(139, 92, 246, 0.1)"
												: "transparent",
											fontWeight: isActive ? 600 : 400,
										}}
										onMouseEnter={(e) => {
											if (!isActive) {
												(e.currentTarget as HTMLDivElement).style.color =
													"#e4e4e7";
												(e.currentTarget as HTMLDivElement).style.background =
													"rgba(255, 255, 255, 0.04)";
											}
										}}
										onMouseLeave={(e) => {
											if (!isActive) {
												(e.currentTarget as HTMLDivElement).style.color =
													"#71717a";
												(e.currentTarget as HTMLDivElement).style.background =
													"transparent";
											}
										}}
									>
										<Icon size={15} style={{ flexShrink: 0, opacity: isActive ? 0.9 : 0.5 }} />
										<span className="flex-1 truncate">{t(label)}</span>
										{count !== undefined && count > 0 && (
											<span
												className="text-[10px] font-mono"
												style={{ color: isActive ? "#8b5cf6" : "#52525b" }}
											>
												{count}
											</span>
										)}
									</div>
								)}
							</NavLink>
						);
					})}
				</div>
			))}
		</>
	);
}

function ProjectChrome({
	slug,
	mobileNavOpen,
	setMobileNavOpen,
}: {
	slug: string;
	mobileNavOpen: boolean;
	setMobileNavOpen: (open: boolean) => void;
}) {
	const { project, items, attacks, recipes, currencies, stats, quests, places, uiTrees } =
		useProjectData();

	const counts: Record<string, number> = {
		items: items.length,
		attacks: attacks.length,
		recipes: recipes.length,
		currencies: currencies.length,
		stats: stats.length,
		quests: quests.length,
		places: places.length,
		uiTrees: uiTrees.length,
	};

	return (
		<>
			<ValidationBanner />
			<div className="flex flex-1 overflow-hidden">
				<aside
					className="hidden md:block w-[220px] shrink-0 overflow-y-auto py-5 px-3"
					style={{
						background: "#111113",
						borderRight: "1px solid rgba(255, 255, 255, 0.06)",
					}}
				>
					<SidebarNav slug={slug} counts={counts} />
				</aside>

				<Dialog.Root open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
					<Dialog.Portal>
						<Dialog.Overlay
							className="md:hidden fixed inset-0 z-40"
							style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
						/>
						<Dialog.Content
							className="md:hidden fixed top-0 left-0 bottom-0 z-50 w-[80vw] max-w-[280px] py-5 px-3 overflow-y-auto"
							style={{
								background: "#111113",
								borderRight: "1px solid rgba(255, 255, 255, 0.06)",
							}}
						>
							<Dialog.Title className="sr-only">Navigation</Dialog.Title>
							<div className="flex justify-end mb-3">
								<Dialog.Close asChild>
									<button
										type="button"
										className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
										style={{ color: "#71717a" }}
										aria-label="Close navigation"
										onMouseEnter={(e) => {
											(e.currentTarget as HTMLButtonElement).style.color = "#e4e4e7";
											(e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.04)";
										}}
										onMouseLeave={(e) => {
											(e.currentTarget as HTMLButtonElement).style.color = "#71717a";
											(e.currentTarget as HTMLButtonElement).style.background = "transparent";
										}}
									>
										<X size={18} />
									</button>
								</Dialog.Close>
							</div>
							<SidebarNav
								slug={slug}
								counts={counts}
								onNavigate={() => setMobileNavOpen(false)}
							/>
						</Dialog.Content>
					</Dialog.Portal>
				</Dialog.Root>

				<main data-demo-project-id={project?.id} className="flex flex-1 flex-col overflow-hidden">
					<Kitsu projectSlug={slug} />
					<div className="min-h-0 flex-1 overflow-hidden">
						<Outlet />
					</div>
				</main>
			</div>
		</>
	);
}

export function ProjectLayout() {
	const { slug } = useParams<{ slug: string }>();
	const navigate = useNavigate();
	const { refresh } = useAuth();
	const [projectLoad, setProjectLoad] = useState<{ slug: string; project: Project | null; error?: unknown } | null>(null);
	const currentLoad = projectLoad?.slug === slug ? projectLoad : null;
	const project = currentLoad?.project ?? null;
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const [tourDismissed, setTourDismissed] = useState(false);
	const showCmsTour = Boolean(
		slug &&
		project &&
		!tourDismissed &&
		new URLSearchParams(window.location.search).get("tour") === "cms",
	);

	useEffect(() => {
		if (!slug) return;
		if (!/^[a-z0-9-]+$/.test(slug)) {
			void navigate("/dashboard", { replace: true });
			return;
		}
		let active = true;
		const controller = new AbortController();
		apiClient.get<Project>(`/api/projects/by-slug/${slug}`, { signal: controller.signal })
			.then((res) => {
				if (active) setProjectLoad({ slug, project: res.data });
			})
			.catch((error) => {
				if (!active) return;
				if (isApiError(error) && error.response?.status === 404) {
					void navigate("/dashboard", { replace: true });
					return;
				}
				setProjectLoad({ slug, project: null, error });
			});
		return () => { active = false; controller.abort(); };
	}, [navigate, slug]);

	function closeCmsTour() {
		setTourDismissed(true);
		void apiClient.put("/api/auth/onboarding")
			.then(() => {
				void refresh();
			})
			.catch((error) => {
				logError("Failed to mark onboarding as complete", error);
			});
	}
	if (currentLoad?.error) throw currentLoad.error;
	if (slug && !project) return <RouteFallback />;

	return (
		<div
			className="flex flex-col h-screen overflow-hidden"
			style={{ background: "#0a0a0b" }}
		>
			<AppHeader
				slug={slug}
				universeId={project?.universeId}
				onBurgerClick={() => setMobileNavOpen(true)}
			/>

			{slug ? (
				<ProjectDataProvider key={slug} slug={slug}>
					<ProjectChrome
						slug={slug}
						mobileNavOpen={mobileNavOpen}
						setMobileNavOpen={setMobileNavOpen}
					/>
				</ProjectDataProvider>
			) : (
				<div className="flex flex-1 overflow-hidden">
					<main className="flex-1 overflow-hidden">
						<Outlet />
					</main>
				</div>
			)}
			<CmsTourDialog open={showCmsTour} onClose={closeCmsTour} />
		</div>
	);
}
